import type { Locale } from '../translations'
import type { SerializedPlace } from '../types'
import type { Coordinate } from './gpx'

const EARTH_RADIUS_METERS = 6371000
const WALKING_SPEED_KILOMETERS_PER_HOUR = 4.5

export interface RouteMeasurement {
  /** Length of the whole route in meters. */
  length: number
  /** Distance along the route from each place to the next one, in meters. */
  legs: number[]
}

export function distanceMeters([longitudeA, latitudeA]: Coordinate, [longitudeB, latitudeB]: Coordinate): number {
  const radians = Math.PI / 180
  const deltaLatitude = (latitudeB - latitudeA) * radians
  const deltaLongitude = (longitudeB - longitudeA) * radians
  const haversine =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(latitudeA * radians) * Math.cos(latitudeB * radians) * Math.sin(deltaLongitude / 2) ** 2

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(haversine))
}

/**
 * Measures the route and the legs between the places. Each place is matched to the nearest route point that comes
 * after the previous place's point, so the route must visit the places in list order.
 */
export function measureRoute(coordinates: Coordinate[], places: SerializedPlace[]): RouteMeasurement {
  const lengthUntil = [0]
  for (let index = 1; index < coordinates.length; index++) {
    lengthUntil.push(lengthUntil[index - 1] + distanceMeters(coordinates[index - 1], coordinates[index]))
  }

  let searchFrom = 0
  const placeIndexes = places.map((place) => {
    const position: Coordinate = [place.longitude, place.latitude]
    let nearest = searchFrom
    for (let index = searchFrom; index < coordinates.length; index++) {
      if (distanceMeters(coordinates[index], position) < distanceMeters(coordinates[nearest], position)) nearest = index
    }
    searchFrom = nearest
    return nearest
  })

  return {
    length: lengthUntil[lengthUntil.length - 1],
    legs: placeIndexes.slice(1).map((placeIndex, index) => lengthUntil[placeIndex] - lengthUntil[placeIndexes[index]]),
  }
}

export function walkingMinutes(meters: number): number {
  return Math.max(1, Math.round((meters / 1000 / WALKING_SPEED_KILOMETERS_PER_HOUR) * 60))
}

/** Walking directions through all places. Google Maps allows up to 9 stops between origin and destination. */
export function googleMapsDirectionsUrl(places: SerializedPlace[]): string {
  const position = (place: SerializedPlace) => `${place.latitude},${place.longitude}`
  const parameters = new URLSearchParams({
    api: '1',
    origin: position(places[0]),
    destination: position(places[places.length - 1]),
    waypoints: places.slice(1, -1).map(position).join('|'),
    travelmode: 'walking',
  })

  return `https://www.google.com/maps/dir/?${parameters}`
}

// Current CLDR data writes Latin digits for `ar`, so Arabic asks for Arabic-Indic digits explicitly.
function numberLocale(locale: Locale): string {
  return locale === 'ar' ? 'ar-u-nu-arab' : locale
}

export function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(numberLocale(locale)).format(value)
}

/** Meters rounded to tens below one kilometer, kilometers with one decimal above. */
export function formatDistance(meters: number, locale: Locale): string {
  if (meters < 1000) {
    return new Intl.NumberFormat(numberLocale(locale), { style: 'unit', unit: 'meter' }).format(Math.round(meters / 10) * 10)
  }

  return new Intl.NumberFormat(numberLocale(locale), { style: 'unit', unit: 'kilometer', maximumFractionDigits: 1 }).format(meters / 1000)
}

export function formatMinutes(minutes: number, locale: Locale): string {
  return new Intl.NumberFormat(numberLocale(locale), { style: 'unit', unit: 'minute' }).format(minutes)
}
